import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  SparklesIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  LightBulbIcon,
  ArrowPathIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

const MAX_CRITERIA = 8;

/**
 * Resume Scoring Criteria Configuration.
 *
 * Supports two modes:
 *   1. **Create mode** — pass `jobId` + `onCriteriaChange`. Criteria are managed in parent state.
 *      Generate/suggest APIs use the preview endpoints that only need a jobId.
 *   2. **Edit mode** — pass `screeningId` + optional `onConfigSaved`. Criteria are managed
 *      internally via the screening-scoped API endpoints, with an explicit Save button.
 */
export default function ResumeScoreConfig({
  screeningId,
  jobId,
  criteria: externalCriteria,
  onCriteriaChange,
  onConfigSaved,
  readOnly = false,
}) {
  const isCreateMode = !screeningId && !!jobId;
  const isReadOnly = !!readOnly;

  const [internalCriteria, setInternalCriteria] = useState([]);
  const criteria =
    (isReadOnly && Array.isArray(externalCriteria) && externalCriteria) ||
    (isCreateMode ? (externalCriteria || []) : internalCriteria);
  const setCriteria = isCreateMode
    ? (updater) => {
        const next = typeof updater === 'function' ? updater(externalCriteria || []) : updater;
        onCriteriaChange?.(next);
      }
    : setInternalCriteria;

  const [generating, setGenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newText, setNewText] = useState('');
  const [hasLoaded, setHasLoaded] = useState(isCreateMode);
  const [dirty, setDirty] = useState(false);

  const loadExistingConfig = useCallback(async () => {
    if (isCreateMode) return;
    try {
      const res = await axios.get(`/api/screenings/${screeningId}/resume-scoring/config`);
      const config = res.data?.config;
      if (config?.criteria && config.criteria.length > 0) {
        setInternalCriteria(config.criteria);
      }
      setHasLoaded(true);
    } catch {
      setHasLoaded(true);
    }
  }, [screeningId, isCreateMode]);

  useEffect(() => {
    if (screeningId && !isCreateMode) loadExistingConfig();
  }, [screeningId, isCreateMode, loadExistingConfig]);

  const autoGenTriggered = React.useRef(false);
  useEffect(() => {
    if (isCreateMode && criteria.length === 0 && !generating && !autoGenTriggered.current) {
      autoGenTriggered.current = true;
      handleGenerate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let res;
      if (isCreateMode) {
        res = await axios.post('/api/screenings/generate-criteria-preview', { jobId });
      } else {
        res = await axios.post(
          `/api/screenings/${screeningId}/resume-scoring/generate-criteria`
        );
      }
      setCriteria(res.data.criteria || []);
      if (!isCreateMode) setDirty(true);
      toast.success('Criteria generated from job description');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate criteria');
    } finally {
      setGenerating(false);
    }
  };

  const handleSuggestMore = async () => {
    setSuggesting(true);
    setSuggestions([]);
    try {
      let res;
      if (isCreateMode) {
        res = await axios.post('/api/screenings/suggest-more-preview', {
          jobId,
          criteria,
        });
      } else {
        res = await axios.post(
          `/api/screenings/${screeningId}/resume-scoring/suggest-more`,
          { criteria }
        );
      }
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to get suggestions');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (isCreateMode) return;
    setSaving(true);
    try {
      await axios.put(`/api/screenings/${screeningId}/resume-scoring/config`, {
        criteria,
      });
      setDirty(false);
      toast.success('Scoring criteria saved');
      onConfigSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save criteria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    setCriteria((prev) => prev.filter((c) => c.id !== id));
    if (!isCreateMode) setDirty(true);
  };

  const handleStartEdit = (criterion) => {
    setEditingId(criterion.id);
    setEditText(criterion.text);
  };

  const handleSaveEdit = () => {
    if (!editText.trim() || editText.trim().length < 5) {
      toast.error('Criterion must be at least 5 characters');
      return;
    }
    setCriteria((prev) =>
      prev.map((c) =>
        c.id === editingId ? { ...c, text: editText.trim(), source: 'user_edited' } : c
      )
    );
    setEditingId(null);
    setEditText('');
    if (!isCreateMode) setDirty(true);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleAddNew = () => {
    if (!newText.trim() || newText.trim().length < 5) {
      toast.error('Criterion must be at least 5 characters');
      return;
    }
    if (criteria.length >= MAX_CRITERIA) {
      toast.error(`Maximum ${MAX_CRITERIA} criteria allowed`);
      return;
    }
    setCriteria((prev) => [
      ...prev,
      {
        id: `user_${Date.now()}`,
        text: newText.trim(),
        source: 'user_defined',
      },
    ]);
    setNewText('');
    setAddingNew(false);
    if (!isCreateMode) setDirty(true);
  };

  const handleAcceptSuggestion = (suggestion) => {
    if (criteria.length >= MAX_CRITERIA) {
      toast.error(`Maximum ${MAX_CRITERIA} criteria allowed`);
      return;
    }
    setCriteria((prev) => [...prev, { ...suggestion, source: 'ai_suggested' }]);
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    if (!isCreateMode) setDirty(true);
  };

  const handleDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;

    setCriteria((prev) => {
      const next = [...prev];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return next;
    });
    if (!isCreateMode) setDirty(true);
  };

  if (!hasLoaded) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500">Loading configuration...</span>
      </div>
    );
  }

  if (isReadOnly) {
    return (
      <div className="space-y-2">
        {criteria.length === 0 ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
            <p className="text-sm text-gray-600">No criteria configured yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {criteria.map((criterion, index) => (
              <div
                key={criterion.id || index}
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-white"
              >
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center font-medium mt-0.5">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{criterion.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {criterion.source === 'user_defined' && (
                      <span className="text-[10px] text-gray-400">Custom</span>
                    )}
                    {criterion.source === 'user_edited' && (
                      <span className="text-[10px] text-gray-400">Edited</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Drag to reorder · Max {MAX_CRITERIA}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="regenerate-criteria-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            title="Regenerate criteria from JD"
          >
            {generating ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
                Generating...
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-3.5 w-3.5" />
                Regenerate
              </>
            )}
          </button>
          {criteria.length < MAX_CRITERIA && (
            <button
              type="button"
              onClick={() => setAddingNew(true)}
              disabled={generating}
              data-testid="add-manual-criteria-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-50 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add manually
            </button>
          )}
          {!isCreateMode && dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-3.5 w-3.5" />
                  Save
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Criteria List */}
      {criteria.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="resume-criteria">
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                className="space-y-2"
              >
                {criteria.map((criterion, index) => (
                  <Draggable key={criterion.id} draggableId={String(criterion.id)} index={index}>
                    {(draggableProvided, snapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        className={`group flex items-start gap-3 p-3 rounded-lg border bg-white transition-colors ${
                          snapshot.isDragging
                            ? 'border-indigo-200 ring-2 ring-indigo-100'
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <button
                          type="button"
                          {...draggableProvided.dragHandleProps}
                          className="flex-shrink-0 mt-0.5 p-1 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                          title="Drag to reorder"
                          aria-label="Drag to reorder"
                        >
                          <Bars3Icon className="h-4 w-4" />
                        </button>

                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center font-medium mt-0.5">
                          {index + 1}
                        </span>

                        <div className="flex-1 min-w-0">
                          {editingId === criterion.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                className="flex-1 text-sm border border-blue-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="p-1 text-gray-400 hover:bg-gray-50 rounded"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-gray-800">{criterion.text}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {criterion.source === 'user_defined' && (
                                  <span className="text-[10px] text-gray-400">Custom</span>
                                )}
                                {criterion.source === 'user_edited' && (
                                  <span className="text-[10px] text-gray-400">Edited</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {editingId !== criterion.id && (
                          <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => handleStartEdit(criterion)}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(criterion.id)}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Inline add input (shown when "Add manually" is clicked) */}
      {addingNew && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Type your criterion..."
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddNew();
              if (e.key === 'Escape') {
                setAddingNew(false);
                setNewText('');
              }
            }}
          />
          <button
            type="button"
            onClick={handleAddNew}
            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setAddingNew(false);
              setNewText('');
            }}
            className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Suggest more */}
      {criteria.length > 0 && criteria.length < MAX_CRITERIA && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={handleSuggestMore}
            disabled={suggesting}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-dashed border-indigo-200 text-indigo-500 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50 transition-colors"
          >
            {suggesting ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
                Thinking...
              </>
            ) : (
              <>
                <LightBulbIcon className="h-3.5 w-3.5" />
                Suggest more
              </>
            )}
          </button>
        </div>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="border border-indigo-100 rounded-lg p-3 bg-indigo-50/50 space-y-2">
          <p className="text-xs font-medium text-indigo-700">AI Suggestions</p>
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="flex items-center gap-2 bg-white rounded-md p-2 border border-indigo-100"
            >
              <p className="flex-1 text-sm text-gray-700">{suggestion.text}</p>
              <button
                type="button"
                onClick={() => handleAcceptSuggestion(suggestion)}
                disabled={criteria.length >= MAX_CRITERIA}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <PlusIcon className="h-3 w-3" />
                Add
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {criteria.length === 0 && !generating && !addingNew && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <SparklesIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-1">No criteria configured yet</p>
          <p className="text-xs text-gray-400">
            Criteria will be auto-generated from the job description, or click &quot;Add manually&quot; to define your own.
          </p>
        </div>
      )}
    </div>
  );
}
